import { useCallback, useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { logQuietError } from "@/lib/quiet-error";
import { Button } from "@/components/ui/button";
import {
  Coins,
  ArrowDownToLine,
  ArrowUpRight,
  Gift,
  Loader2,
  Sparkles,
  ShieldCheck,
  Zap,
  History,
  AlertTriangle,
  Check,
  Info,
} from "lucide-react";
import Sparkline from "@/components/Sparkline";
import { Skeleton, SkeletonRow } from "@/components/Skeleton";
import { toast } from "sonner";
import PlanUpgradeSection from "@/components/PlanUpgradeSection";

const TXN_META = {
  signup_bonus: { label: "Welcome bonus",  icon: Gift,        chip: "tb-chip-mint" },
  recharge:     { label: "Recharge",       icon: ArrowDownToLine, chip: "tb-chip-lavender" },
  spend:        { label: "Used",           icon: ArrowUpRight, chip: "tb-chip-sky" },
  reward:       { label: "Reward",         icon: Sparkles,     chip: "tb-chip-butter" },
  refund:       { label: "Refund",         icon: Check,        chip: "tb-chip-mint" },
  admin_grant:  { label: "Admin grant",    icon: ShieldCheck,  chip: "tb-chip-lavender" },
};

export default function Wallet() {
  const [wallet, setWallet] = useState(null);
  const [txns, setTxns] = useState(null);
  const [packs, setPacks] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [recharging, setRecharging] = useState(null);
  const [paymentConsent, setPaymentConsent] = useState(false);

  const reload = useCallback(async () => {
    try {
      const [w, t, p, pr] = await Promise.all([
        api.get("/coins/wallet"),
        api.get("/coins/transactions"),
        api.get("/coins/packs"),
        api.get("/coins/pricing"),
      ]);
      setWallet(w.data);
      setTxns(t.data);
      setPacks(p.data);
      setPricing(pr.data);
    } catch (e) {
      logQuietError("wallet.reload", e);
      toast.error("Could not load wallet");
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const recharge = async (packId, provider = "razorpay") => {
    if (!paymentConsent) {
      toast.error("Please agree to the payment terms before proceeding");
      return;
    }
    setRecharging(packId);
    try {
      // Idempotency key — locks this exact purchase intent so repeat clicks/network
      // retries don't double-credit. Backend dedupes via metadata.idempotency_key.
      const idempotency_key = `chk-${packId}-${Date.now()}-${crypto.randomUUID().slice(0, 8)}`;
      const { data } = await api.post("/coins/recharge/create-checkout", {
        pack_id: packId,
        provider,
        idempotency_key,
      });
      if (data.mocked) {
        toast.success(`+${data.coins} TeeBee Coins added (demo recharge)`, {
          description: "Real Razorpay payment activates once production keys are wired.",
        });
      } else {
        window.location.href = data.checkout_url;
        return;
      }
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Recharge failed");
    } finally {
      setRecharging(null);
    }
  };

  if (!wallet) {
    return (
      <Layout title="TeeBee Wallet">
        <div className="space-y-4 max-w-4xl">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </Layout>
    );
  }

  // Build sparkline from monthly_usage
  const monthly = wallet.monthly_usage || [];
  const sparkData = monthly.length
    ? monthly.map((m) => ({ day: m.month, count: m.spent }))
    : [{ day: "", count: 0 }, { day: "", count: 0 }];

  return (
    <Layout title="TeeBee Wallet">
      {/* ===== Header ===== */}
      <div className="mb-6">
        <span className="tb-chip-butter inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide">
          <Coins className="h-3 w-3" /> Wallet
        </span>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight font-medium leading-tight">
          TeeBee Coins
        </h1>
        <p className="mt-1.5 text-sm text-muted-foreground max-w-xl">
          Internal AI usage credits. Not crypto. Not currency. Powers every lesson, scan and workflow.
        </p>
      </div>

      {/* ===== Balance hero ===== */}
      <div className="tb-card-feature relative overflow-hidden mb-6" data-testid="wallet-hero">
        <div className="absolute inset-0 tb-grid-bg opacity-50 pointer-events-none" />
        <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-muted-foreground inline-flex items-center gap-2">
              <Coins className="h-3 w-3" /> Current balance
            </p>
            <div className="mt-2 flex items-baseline gap-3">
              <p className="font-display text-5xl sm:text-6xl tracking-tight font-medium">{wallet.balance.toLocaleString()}</p>
              <p className="text-sm text-muted-foreground">coins</p>
            </div>
            {wallet.low_balance_warning && (
              <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-amber-100 dark:bg-amber-500/15 text-amber-800 dark:text-amber-200 px-3 py-1.5 text-xs" data-testid="wallet-low-warning">
                <AlertTriangle className="h-3.5 w-3.5" /> Low balance — recharge to keep generating.
              </div>
            )}
            <div className="mt-5 grid grid-cols-3 gap-3 max-w-md">
              <Mini label="Earned"    value={wallet.total_earned} />
              <Mini label="Spent"     value={wallet.total_spent} />
              <Mini label="Recharged" value={wallet.total_recharged} />
            </div>
          </div>
          <div className="rounded-2xl bg-muted/40 p-4 border border-border">
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground inline-flex items-center gap-2">
              <Zap className="h-3 w-3" /> Last 6 months
            </p>
            <Sparkline data={sparkData} height={80} className="mt-3" />
            <p className="mt-3 text-[11px] text-muted-foreground text-center">
              {monthly.length ? `${monthly[monthly.length - 1]?.spent || 0} used this month` : "No usage yet"}
            </p>
          </div>
        </div>
      </div>

      {/* ===== Recharge packs ===== */}
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-display text-xl font-medium tracking-tight">Recharge with TeeBee Coins</h2>
        <span className="text-[10px] text-muted-foreground inline-flex items-center gap-1.5">
          <Info className="h-3 w-3" /> Razorpay integration coming · demo recharge live
        </span>
      </div>

      {/* Razorpay-required payment disclosure + consent (required before purchase) */}
      <div className="rounded-2xl border border-border bg-card p-4 mb-4" data-testid="payment-disclosure">
        <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">
          Payment terms · INR · Razorpay
        </p>
        <ul className="mt-2 space-y-1 text-[12px] text-foreground/80 list-disc list-inside marker:text-muted-foreground">
          <li>Payments are processed securely through Razorpay. All transactions are in Indian Rupees (INR).</li>
          <li>TeeBee Coins are internal platform credits only — not crypto, not cash, not investment, not withdrawable.</li>
          <li>Used coins are generally non-refundable. Failed/duplicate payments may be reviewed for refund.</li>
        </ul>
        <label className="mt-3 flex items-start gap-2 text-[12px] cursor-pointer" data-testid="payment-consent-row">
          <input
            type="checkbox"
            checked={paymentConsent}
            onChange={(e) => setPaymentConsent(e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-violet-600 cursor-pointer"
            data-testid="payment-consent-checkbox"
          />
          <span className="text-muted-foreground leading-relaxed">
            I agree to TeeBee&apos;s{" "}
            <a href="/legal/terms" className="text-foreground underline-offset-2 hover:underline">Terms</a>,{" "}
            <a href="/legal/refund-policy" className="text-foreground underline-offset-2 hover:underline">Refund Policy</a>,{" "}
            <a href="/legal/pricing-billing" className="text-foreground underline-offset-2 hover:underline">Pricing &amp; Billing</a>{" "}
            and the{" "}
            <a href="/legal/coins-policy" className="text-foreground underline-offset-2 hover:underline">TeeBee Coins Policy</a>.
          </span>
        </label>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8 tb-stagger" data-testid="recharge-packs">
        {packs.map((p) => {
          const perCoin = (p.price_inr / (p.coins + (p.bonus || 0))).toFixed(2);
          return (
            <div key={p.pack_id} className={`tb-card p-5 relative ${p.popular ? "tb-tracing-border" : ""}`} data-testid={`recharge-pack-${p.pack_id}`}>
              {p.popular && (
                <span className="absolute top-3 right-3 tb-chip-lavender text-[10px] uppercase tracking-[0.18em] font-semibold rounded-full px-2 py-0.5">Popular</span>
              )}
              <p className="text-xs uppercase tracking-[0.22em] font-semibold text-muted-foreground">{p.name}</p>
              <div className="mt-2 flex items-baseline gap-1">
                <p className="font-display text-3xl tracking-tight font-medium">{p.coins.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">coins</p>
              </div>
              {p.bonus > 0 && (
                <p className="mt-1 text-[11px] text-emerald-600 dark:text-emerald-400 font-medium">
                  +{p.bonus} bonus coins
                </p>
              )}
              <p className="mt-3 text-[11px] text-muted-foreground">{p.tag}</p>
              <p className="mt-1 font-medium text-sm">
                ₹{p.price_inr.toLocaleString()}
                <span className="ml-1.5 text-[11px] text-muted-foreground font-normal">· ₹{perCoin}/coin</span>
              </p>
              <Button
                className="mt-4 w-full rounded-full"
                variant={p.popular ? "default" : "outline"}
                onClick={() => recharge(p.pack_id)}
                disabled={!!recharging || !paymentConsent}
                data-testid={`recharge-cta-${p.pack_id}`}
              >
                {recharging === p.pack_id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <ArrowDownToLine className="h-3.5 w-3.5 mr-1.5" />}
                {paymentConsent ? "Recharge" : "Agree to continue"}
              </Button>
            </div>
          );
        })}
      </div>

      {/* ===== Transactions + Pricing ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Transactions */}
        <div className="lg:col-span-7 tb-card p-5" data-testid="wallet-transactions">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-medium tracking-tight inline-flex items-center gap-2">
              <History className="h-4 w-4 text-muted-foreground" /> Transactions
            </h3>
            <p className="text-[11px] text-muted-foreground">Last 50</p>
          </div>
          {txns === null ? (
            <div className="space-y-2"><SkeletonRow /><SkeletonRow /><SkeletonRow /></div>
          ) : txns.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No transactions yet.</p>
          ) : (
            <ul className="divide-y divide-border">
              {txns.map((t) => {
                const meta = TXN_META[t.kind] || TXN_META.spend;
                const Icon = meta.icon;
                const positive = t.amount > 0;
                return (
                  <li key={t.txn_id} className="py-3 flex items-center gap-3" data-testid={`txn-${t.txn_id}`}>
                    <span className={`inline-grid place-items-center h-9 w-9 rounded-xl ${meta.chip}`}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{meta.label}{t.feature ? <span className="text-muted-foreground"> · {t.feature.replace(/_/g, " ")}</span> : null}</p>
                      <p className="text-[11px] text-muted-foreground">{new Date(t.created_at).toLocaleString()}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className={`font-mono text-sm font-medium ${positive ? "text-emerald-600 dark:text-emerald-400" : "text-foreground"}`}>
                        {positive ? "+" : ""}{t.amount}
                      </p>
                      <p className="text-[10px] text-muted-foreground">bal: {t.balance_after}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Pricing table */}
        <div className="lg:col-span-5 tb-card p-5" data-testid="wallet-pricing">
          <h3 className="font-display text-lg font-medium tracking-tight">How coins are spent</h3>
          <p className="mt-1 text-xs text-muted-foreground">Transparent pricing — surfaced before every generation.</p>
          {!pricing ? (
            <Skeleton className="h-40 mt-4" />
          ) : (
            <ul className="mt-4 divide-y divide-border">
              {pricing.features.map((f) => (
                <li key={f.key} className="py-2 flex items-center justify-between">
                  <span className="text-sm">{f.label}</span>
                  <span className="font-mono text-xs text-muted-foreground">{f.cost} coins</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* ===== Plan upgrade — beats one-off recharges for power users ===== */}
      <PlanUpgradeSection source="wallet" id="plans" />
    </Layout>
  );
}

function Mini({ label, value }) {
  return (
    <div>
      <p className="font-display text-lg tracking-tight font-medium">{(value || 0).toLocaleString()}</p>
      <p className="text-[10px] text-muted-foreground uppercase tracking-[0.18em]">{label}</p>
    </div>
  );
}
